/**
 * GlobalStylesPanel — Sidebar panel for editing store-wide theme settings.
 *
 * Controls: brand color, font family, button style, section spacing.
 * All changes propagate to the live preview via PostMessage in real time.
 */

import { ArrowLeft } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { ColorControl } from './controls'
import { PresetControl } from './controls'
import {
  FONT_OPTIONS,
  BUTTON_STYLE_OPTIONS,
  SPACING_OPTIONS,
  MAX_WIDTH_PRESETS,
} from './theme-utils'
import type { GlobalStyles } from './useGlobalStyles'

interface GlobalStylesPanelProps {
  styles: GlobalStyles
  onBrandColorChange: (color: string | undefined) => void
  onFontFamilyChange: (font: string) => void
  onButtonStyleChange: (style: string) => void
  onSectionSpacingChange: (spacing: string) => void
  onPageMaxWidthChange: (width: number | undefined) => void
  onBack: () => void
}

export function GlobalStylesPanel({
  styles,
  onBrandColorChange,
  onFontFamilyChange,
  onButtonStyleChange,
  onSectionSpacingChange,
  onPageMaxWidthChange,
  onBack,
}: GlobalStylesPanelProps) {
  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={onBack}
          className="rounded-xs p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="text-sm font-semibold text-gray-900">Theme</h2>
      </div>

      <div className="space-y-6">
        {/* Brand Color */}
        <ColorControl
          value={styles.brandColor}
          onChange={onBrandColorChange}
          label="Brand Color"
        />

        {/* Font Family */}
        <div className="space-y-1.5">
          <Label className="text-[13px] font-medium text-gray-600">
            Font Family
          </Label>
          <div className="flex flex-col gap-1">
            {FONT_OPTIONS.map((opt) => {
              const isSelected = styles.fontFamily === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onFontFamilyChange(opt.value)}
                  className={`rounded-sm px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-200'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  style={{ fontFamily: opt.css }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Button Style */}
        <div className="space-y-1.5">
          <Label className="text-[13px] font-medium text-gray-600">
            Button Style
          </Label>
          <div className="flex gap-2">
            {BUTTON_STYLE_OPTIONS.map((opt) => {
              const isSelected = styles.buttonStyle === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onButtonStyleChange(opt.value)}
                  className={`flex flex-1 flex-col items-center gap-1.5 rounded-sm border-2 px-3 py-3 text-xs transition-colors ${
                    isSelected
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {/* Mini button preview */}
                  <span
                    className="inline-block h-6 w-14 bg-sky-500"
                    style={{ borderRadius: opt.radius }}
                  />
                  <span className="font-medium">{opt.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Section Spacing */}
        <PresetControl
          value={styles.sectionSpacing}
          onChange={onSectionSpacingChange}
          label="Section Spacing"
          options={SPACING_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />

        {/* Page Max Width */}
        <div className="space-y-1.5">
          <Label className="text-[13px] font-medium text-gray-600">
            Page Max Width
          </Label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={320}
              max={1920}
              step={10}
              value={styles.pageMaxWidth ?? 1920}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                onPageMaxWidthChange(v >= 1920 ? undefined : v)
              }}
              className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-sky-500"
            />
            <span className="w-14 text-right text-xs tabular-nums text-gray-500">
              {styles.pageMaxWidth ? `${styles.pageMaxWidth}px` : 'Full'}
            </span>
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onPageMaxWidthChange(undefined)}
              className={`rounded-xs px-2 py-1 text-xs transition-colors ${
                !styles.pageMaxWidth
                  ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Full
            </button>
            {MAX_WIDTH_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => onPageMaxWidthChange(p.value)}
                className={`rounded-xs px-2 py-1 text-xs transition-colors ${
                  styles.pageMaxWidth === p.value
                    ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
